/* GridColumnRenderer.js */

"use strict";

const _ = require('lodash');

class GridColumnRenderer {
    constructor(model) {
        this._model = model;
        this._rootElem = document.createElement("grid-column");
        this._rootElem.component = this._model;
        if (!this._model._isGroupingColumn && !this._model._isGroupOnlyColumn) {
            this._rootElem.setAttribute('draggable', true);
        }
        this._resizeHandle = document.createElement('resize-handle');
        this._columnName = document.createElement('div');
        this._columnName.classList.add('column-name');
        this._iconContainer = document.createElement('div');
        this._iconContainer.classList.add('icon-container');
        this._sortIcon = document.createElement('toggle-icon-gl');
        this._sortIcon.dataset.icon = 'toggle.sort';
        this._lockIcon = document.createElement('toggle-icon-gl');
        this._lockIcon.dataset.icon = 'toggle.lockunlock';
        this._lockIcon.setToggled(true);
        if (!this._model._isGroupOnlyColumn) {
            this._iconContainer.appendChild(this._sortIcon);
        }
        if (!this._model._isGroupingColumn && !this._model._isGroupOnlyColumn && !this._model._isLockedRight) {
            this._iconContainer.appendChild(this._lockIcon);
        }
        this._rootElem.appendChild(this._columnName);
        this._rootElem.appendChild(this._iconContainer);

        this._mouseMoveTimeout = undefined;
        this._dragCounter = 0;
        this._swappedColumnElement = undefined;

        this._registerListeners();
        this.render();
    }

    /********************************************/
    /* START PRIVATE COMPONENT RENDERER METHODS */
    /********************************************/
    _shutdown() {
        let prom = new Promise((resolve /*,reject*/) => {
            this._unregisterListeners();
            this._model = undefined;
            this._rootElem.remove();
            this._rootElem = undefined;
            this._resizeHandle = undefined;
            this._columnName = undefined;
            this._iconContainer = undefined;
            this._sortIcon = undefined;
            this._lockIcon = undefined;
            this._mouseMoveTimeout = undefined;
            this._dragCounter = undefined;
            this._swappedColumnElement = undefined;
            resolve();
        });
        return prom;
    }

    _registerListeners() {
        this._model.on("ID", this._onId = this._onId.bind(this));
        this._model.on("Width", this._onWidth = this._onWidth.bind(this));
        this._model.on("ShowHeader", this._onShowHeader = this._onShowHeader.bind(this));
        this._model.on("Label", this._onLabel = this._onLabel.bind(this));
        this._model.on("Description", this._onDescription = this._onDescription.bind(this));
        this._model.on("Resizable", this._onResizable = this._onResizable.bind(this));
        this._model.on("Sortable", this._onSortable = this._onSortable.bind(this));
        this._model.on("Sort", this._onSort = this._onSort.bind(this));
        this._model.on("Locked", this._onLocked = this._onLocked.bind(this));
        this._model.on("Shown", this._onShown = this._onShown.bind(this));
        this._model.on("ColumnRendererComponent", this._onColumnRendererComponent = this._onColumnRendererComponent.bind(this));
        if (!this._model._isGroupingColumn && !this._model._isGroupOnlyColumn && this._model._reorder) {
            this._rootElem.addEventListener('dragstart', this._onDragStartEventHandler = this._onDragStartEventHandler.bind(this));
            this._rootElem.addEventListener("dragend", this._onDragEndEventHandler = this._onDragEndEventHandler.bind(this));
            this._rootElem.addEventListener("dragenter", this._onDragEnterEventHandler = this._onDragEnterEventHandler.bind(this));
            this._rootElem.addEventListener("dragover", this._onDragOverEventHandler = this._onDragOverEventHandler.bind(this));
            this._rootElem.addEventListener("dragleave", this._onDragLeaveEventHandler = this._onDragLeaveEventHandler.bind(this));
            this._rootElem.addEventListener("drop", this._onDropEventHandler = this._onDropEventHandler.bind(this));
        }
        this._rootElem.addEventListener('click', this._onRootElementClick = this._onRootElementClick.bind(this));
        this._resizeHandle.addEventListener('mousedown', this._onResizeHandleMouseDown = this._onResizeHandleMouseDown.bind(this));
        this._resizeHandle.addEventListener('click', this._onResizeHandleMouseClick = this._onResizeHandleMouseClick.bind(this));
        this._resizeHandle.addEventListener('dblclick', this._onResizeHandleMouseDblclick = this._onResizeHandleMouseDblclick.bind(this));
        this._lockIcon.addEventListener('click', this._onLockIconClick = this._onLockIconClick.bind(this));
        this._lockIcon.addEventListener('mouseover', this._onLockIconMouseOver = this._onLockIconMouseOver.bind(this));
        this._lockIcon.addEventListener('mouseleave', this._onLockIconMouseLeave = this._onLockIconMouseLeave.bind(this));
    }

    _unregisterListeners() {
        this._model.removeListener("ID", this._onId);
        this._model.removeListener("Width", this._onWidth);
        this._model.removeListener("ShowHeader", this._onShowHeader);
        this._model.removeListener("Label", this._onLabel);
        this._model.removeListener("Description", this._onDescription);
        this._model.removeListener("Resizable", this._onResizable);
        this._model.removeListener("Sortable", this._onSortable);
        this._model.removeListener("Sort", this._onSort);
        this._model.removeListener("Locked", this._onLocked);
        this._model.removeListener("Shown", this._onShown);
        this._model.removeListener("ColumnRendererComponent", this._onColumnRendererComponent);
        if (!this._model._isGroupingColumn && !this._model._isGroupOnlyColumn && this._model._reorder) {
            this._rootElem.removeEventListener('dragstart', this._onDragStartEventHandler);
            this._rootElem.removeEventListener("dragend", this._onDragEndEventHandler);
            this._rootElem.removeEventListener("dragenter", this._onDragEnterEventHandler);
            this._rootElem.removeEventListener("dragover", this._onDragOverEventHandler);
            this._rootElem.removeEventListener("dragleave", this._onDragLeaveEventHandler);
            this._rootElem.removeEventListener("drop", this._onDropEventHandler);
        }
        this._rootElem.removeEventListener('click', this._onRootElementClick);
        this._resizeHandle.removeEventListener('mousedown', this._onResizeHandleMouseDown);
        this._lockIcon.removeEventListener('click', this._onLockIconClick);
        this._lockIcon.removeEventListener('mouseover', this._onLockIconMouseOver);
        this._lockIcon.removeEventListener('mouseleave', this._onLockIconMouseLeave);
    }

    /*******************************************/
    /* END PRIVATE COMPONENT RENDERER METHODS  */
    /* START PUBLIC COMPONENT RENDERER METHODS */
    /*******************************************/
    /**
     * Stateless method to completely rebuild this component from the _rootElem down.
     * @returns {Promise} An ES6 promise that lets the caller know when the component has finished rendering. If a non
     * Promise is returned the caller will assume the Renderer has completed rendering.
     */
    render() {
        let prom = new Promise((resolve/*,reject*/) => {
            this._setId(this._model.getId());
            this._setWidth(this._model.getWidth());
            this._setShowHeader(this._model.getShowHeader());
            this._setLabel(this._model.getLabel());
            this._setDescription(this._model._description);
            this._setResizable(this._model.getResizable());
            this._setSortable(this._model.getSortable());
            this._setSort(this._model.getSort());
            this._setLocked(this._model.getLocked());
            this._setShown(this._model.getShown());
            this._setColumnRendererComponent(this._model.getColumnRendererComponent());
            resolve();
        });
        return prom;
    }

    get rootElement() {
        return this.getRootElement();
    }

    getRootElement() {
        return this._rootElem;
    }

    get model() {
        return this.getModel();
    }

    getModel() {
        return this._model;
    }

    /*******************************************/
    /* END COMPONENT RENDERER METHODS */
    /* START HANDLER METHODS */
    /*******************************************/

    /**
     * Fired when the id has been updated in the model
     * @param newVal {string}
     * @private
     */
    _onId(newVal) {
        this._setId(newVal);
    }

    /**
     * Fired when the width has been updated in the model
     * @param newVal {number}
     * @private
     */
    _onWidth(newVal) {
        this._setWidth(newVal);
    }

    /**
     * Fired when the show header state has been updated in the model
     * @param newVal {boolean}
     * @private
     */
    _onShowHeader(newVal) {
        this._setShowHeader(newVal);
    }

    /**
     * Fired when the label has been updated in the model
     * @param newVal {string}
     * @private
     */
    _onLabel(newVal) {
        this._setLabel(newVal);
    }

    /**
     * Fired when the description has been updated in the model
     * @param newVal {string}
     * @private
     */
    _onDescription(newVal) {
        this._setDescription(newVal);
    }

    /**
     * Fired when the resizable state has been updated in the model
     * @param newVal {boolean}
     * @private
     */
    _onResizable(newVal) {
        this._setResizable(newVal);
    }

    /**
     * Fired when the sortable state has been updated in the model
     * @param newVal {boolean}
     * @private
     */
    _onSortable(newVal) {
        this._setSortable(newVal);
    }

    /**
     * Fired when the sorted state has been updated in the model
     * @param newVal {string}
     * @private
     */
    _onSort(newVal) {
        this._setSort(newVal);
    }

    /**
     * Fired when the locked state has been updated in the model
     * @param newVal {boolean}
     * @private
     */
    _onLocked(newVal) {
        this._setLocked(newVal);
    }

    /**
     * Fired when the shown state has been updated in the model
     * @param newVal {boolean}
     * @private
     */
    _onShown(newVal) {
        this._setShown(newVal);
    }

    /**
     * Fired when columnRendererComponent has been updated in the model
     * @param newVal {component}
     * @private
     */
    _onColumnRendererComponent(newVal) {
        this._setColumnRendererComponent(newVal);
    }

    /**
     * "Drag Start" event handler for the element being dragged. Add drag/drop
     * target classes to the column DOM elements.
     * @param event {Event}
     * @private
     */
    _onDragStartEventHandler(event) {
        event.stopImmediatePropagation();
        event.dataTransfer.effectAllowed = 'move';
        this._rootElem.parentNode.classList.add('drag-active');
        this._rootElem.classList.add('drag-target');
        let columnContainer = this._rootElem.closest('.column-container'),
            columnContainerWidth = this._rootElem.clientWidth,
            sibling = this._rootElem.nextElementSibling;
        while (sibling) {
            sibling.classList.add('potential-drop-target');
            columnContainerWidth += sibling.clientWidth;
            sibling = sibling.nextElementSibling;
        }
        sibling = this._rootElem.previousElementSibling;
        while (sibling) {
            sibling.classList.add('potential-drop-target');
            columnContainerWidth += sibling.clientWidth;
            sibling = sibling.previousElementSibling;
        }
        columnContainer.style.width = columnContainerWidth / 10 + 'rem';
    }

    /**
     * "Drag End" event handler for the element being dragged. Remove drag/drop target
     * classes from the column DOM elements and emit the column reordered event.
     * @param event {Event}
     * @private
     */
    _onDragEndEventHandler(event) {
        event.stopImmediatePropagation();
        let columnContainer = this._rootElem.closest('.column-container'),
            newIndex = this._getCurrentIndexPosition(),
            sibling = this._rootElem.nextElementSibling;
        this._rootElem.parentNode.classList.remove('drag-active');
        this._rootElem.classList.remove('drag-target');
        columnContainer.removeAttribute('style');
        while (sibling) {
            sibling.classList.remove('potential-drop-target', 'drop-target');
            sibling = sibling.nextElementSibling;
        }
        sibling = this._rootElem.previousElementSibling;
        while (sibling) {
            sibling.classList.remove('potential-drop-target', 'drop-target');
            sibling = sibling.previousElementSibling;
        }
        this._model._triggerUserReordered(newIndex);
    }

    /**
     * "Drag Enter" event handler for the element that has another element being dragged into it
     * @param event {Event}
     * @private
     */
    _onDragEnterEventHandler(event) {
        event.stopImmediatePropagation();
        this._dragCounter++;
        if (event.currentTarget.classList.contains('potential-drop-target')) {
            event.currentTarget.classList.add('drop-target');
        }
    }

    /**
     * "Drag Over" event handler for the element that has another element being dragged over it. Specifies
     * WHERE the dragged data can be dropped.
     * @param event {Event}
     * @private
     */
    _onDragOverEventHandler(event) {
        // Prevent default to allow the 'drop' event to fire
        event.stopImmediatePropagation();
        event.preventDefault();
        let dropTargetDomElement;
        if (event.currentTarget.classList.contains('potential-drop-target')) {
            dropTargetDomElement = event.currentTarget;
        }
        if (!dropTargetDomElement) {
            return false;
        }
        if (dropTargetDomElement === this._swappedColumnElement) {
            return false;
        }
        let elOffsetLeft = dropTargetDomElement.getBoundingClientRect().left,
            elWidth = dropTargetDomElement.offsetWidth,
            mouseOffset = event.pageX - elOffsetLeft;
        if (mouseOffset > (elWidth / 2)) {
            this._swapElements(dropTargetDomElement, 'right');
        } else {
            this._swapElements(dropTargetDomElement, 'left');
        }
    }

    /**
     * "Drag Leave" event handler for the element that has another element being dragged out of it
     * @param event {Event}
     * @private
     */
    _onDragLeaveEventHandler(event) {
        event.stopImmediatePropagation();
        this._swappedColumnElement = undefined;
        this._dragCounter--;
        if (this._dragCounter === 0) {
            this._rootElem.classList.remove('drop-target');
        }
    }

    /**
     * "Drop" event handler for the element that has another element dropped into it
     * @param event {Event}
     * @private
     */
    _onDropEventHandler(/*event*/) {
        // Here if needed
    }

    /**
     * Add window events to catch the user's mouse interactions for resizing the column.
     * @param event {Event}
     * @private
     */
    _onResizeHandleMouseDown(event) {
        event.preventDefault();
        this._rootElem.classList.add('resizing');
        window.addEventListener('mousemove', this._onWindowMouseMove = this._onWindowMouseMove.bind(this));
        window.addEventListener('mouseup', this._onWindowMouseUp = this._onWindowMouseUp.bind(this));
    }

    /**
     * When the user releases the mouse on a resize action, remove window-level handlers.
     * @param event {Event}
     * @private
     */
    _onWindowMouseUp(/*event*/) {
        this._rootElem.classList.remove('resizing');
        window.removeEventListener('mousemove', this._onWindowMouseMove);
        window.removeEventListener('mouseup', this._onWindowMouseUp);
    }

    /**
     * _resizeHandle click handler to prevent resort action on column resize
     * @param {Event} event
     * @private
     */
    _onResizeHandleMouseClick(event) {
        event.stopPropagation();
    }

    /**
     *  _resizeHandle double click handler to reset width to its initial state
     * @param {Event} event
     * @private
     */
    _onResizeHandleMouseDblclick(event) {
        event.stopPropagation();
        this._model.setWidth(this._model.getInitialWidth());
        this._model._triggerUserResized(event);
    }

    /**
     * Debounce the the column/cell resizing to minimize jank.
     * @param event {Event}
     * @private
     */
    _onWindowMouseMove(event) {
        if (_.isUndefined(this._mouseMoveTimeout)) {
            this._mouseMoveTimeout = setTimeout(() => {
                let minWidth = this._model.getMinWidth(),
                    xPos = event.pageX,
                    currentXPos = this._rootElem.getBoundingClientRect().right,
                    widthDifference = currentXPos - xPos,
                    currentWidth = this._model.getWidth(),
                    newWidth = (currentWidth - widthDifference > minWidth) ? currentWidth - widthDifference : minWidth;
                this._model.setWidth(newWidth);
                this._model._triggerUserResized(event);
                this._mouseMoveTimeout = undefined;
            }, 50);
        }
    }

    /**
     * Fired when root element has been clicked
     * @param event {event}
     * @private
     */
    _onRootElementClick(event) {
        let sortable = this._model.getSortable();
        if (sortable === true) {
            let sort = this._model.getSort().toLowerCase();
            if (sort === "asc") {
                this._model.setSort('desc');
            } else {
                this._model.setSort('asc');
            }
            this._model._triggerUserSorted(event);
        }
    }

    /**
     * Fired when the lock icon is clicked
     * @param event {Event}
     * @private
     */
    _onLockIconClick(event) {
        event.stopImmediatePropagation();
        this._model.setLocked(!this._model.getLocked());
        this._model._triggerUserLocked(event);
    }

    /**
     * Fired when the lock icon is hovered
     * @private
     */
    _onLockIconMouseOver() {
        this._lockIcon.setToggled(!!this._model.getLocked());
    }

    /**
     * Fired when the lock icon is unhovered
     * @private
     */
    _onLockIconMouseLeave() {
        this._lockIcon.setToggled(!this._model.getLocked());
    }

    /*******************************************/
    /* END HANDLER METHODS */
    /* START PRIVATE API METHODS */

    /*******************************************/

    /**
     * Set the ID as a class in the DOM
     * @param newVal {string}
     * @private
     */
    _setId(newVal) {
        if (!_.isUndefined(newVal)) {
            this._rootElem.classList.add(newVal);
        }
    }

    /**
     * Set the width (in numeric percentage) in the DOM
     * @param newVal {double}
     * @private
     */
    _setWidth(newVal) {
        if (!_.isUndefined(newVal)) {
            if (typeof(newVal) === 'number') {
                this._rootElem.style.width = newVal / 10 + 'rem';
            } else {
                console.error('Specify column width as a Number representing the desired pixel size. It will be converted to REM units.');
            }
        }
    }

    /**
     * Toggle the display of the column header name
     * @param newVal {boolean}
     * @private
     */
    _setShowHeader(newVal) {
        if (newVal === true) {
            this._rootElem.classList.remove('header-hidden');
        } else {
            this._rootElem.classList.add('header-hidden');
        }
    }

    /**
     * Set the column display name
     * @param newVal {string}
     * @private
     */
    _setLabel(newVal) {
        this._columnName.innerHTML = newVal;
    }

    /**
     * Set the column description tooltip
     * @param newVal {string}
     * @private
     */
    _setDescription(newVal) {
        this._rootElem.setAttribute('title', newVal);
    }

    /**
     * Toggle the resizable state
     * @param newVal {boolean}
     * @private
     */
    _setResizable(newVal) {
        if (newVal === true) {
            this._rootElem.classList.add('resizable');
            this._rootElem.appendChild(this._resizeHandle);
        } else {
            this._rootElem.classList.remove('resizable');
            if (this._rootElem.contains(this._resizeHandle)) {
                this._rootElem.removeChild(this._resizeHandle);
            }
        }
    }

    /**
     * Toggle the sortable state
     * @param newVal {boolean}
     * @private
     */
    _setSortable(newVal) {
        if (newVal === true) {
            this._rootElem.classList.remove('unsortable');
        } else {
            this._rootElem.classList.add('unsortable');
        }
    }

    /**
     * Toggle the sorted state
     * @param newVal {string}
     * @private
     */
    _setSort(newVal) {
        switch (newVal.toLowerCase()) {
            case "asc":
                this._rootElem.classList.add('sorted');
                this._rootElem.classList.add('ascending');
                this._sortIcon.setToggled(true);
                break;
            case "desc":
                this._rootElem.classList.add('sorted');
                this._rootElem.classList.remove('ascending');
                this._sortIcon.setToggled(false);
                break;
            default:
                this._rootElem.classList.remove('sorted');
                this._rootElem.classList.remove('ascending');
                this._sortIcon.setToggled(false);
                break;
        }
    }

    /**
     * Toggle the locked state
     * @param newVal {boolean}
     * @private
     */
    _setLocked(newVal) {
        if (newVal === true) {
            this._rootElem.classList.add('locked');
        } else {
            this._rootElem.classList.remove('locked');
        }
        this._lockIcon.setToggled(!newVal);
    }

    /**
     * Toggle the shown state
     * @param newVal {boolean}
     * @private
     */
    _setShown(newVal) {
        if (newVal === true) {
            this._rootElem.classList.remove('hidden');
        } else {
            this._rootElem.classList.add('hidden');
        }
    }

    /**
     * Do something with the columnRendererComponent
     * @param newVal {Boolean}
     * @private
     */
    _setColumnRendererComponent(/*newVal*/) {
        // Here if needed
    }

    /**
     * Returns the DOM index position of the the drag target.
     * @returns {Number}
     * @private
     */
    _getCurrentIndexPosition() {
        let parent = this._rootElem.parentNode,
            dragTarget = parent.querySelector('.drag-target'),
            dragDropElements = parent.querySelectorAll('grid-column');
        for (let el in dragDropElements) {
            if (dragDropElements[el] === dragTarget) {
                return parseInt(el);
            }
        }
    }

    /**
     * Swap the column DOM element with it's sibling DOM element.
     * @param dropTarget {HTMLElement}
     * @param insertLocation {String} - "left" or "right." Represents the drag location that triggered
     * the swap.
     * @private
     */
    _swapElements(dropTarget, insertLocation) {
        this._swappedColumnElement = dropTarget;
        let parent = dropTarget.parentNode,
            dragTarget = parent.querySelector('.drag-target');
        let newDragTarget = parent.removeChild(dragTarget);
        if (insertLocation === 'right') {
            parent.insertBefore(newDragTarget, dropTarget);
        } else {
            parent.insertBefore(newDragTarget, dropTarget.nextSibling);
        }
    }

    /*******************************************/
    /*  END PRIVATE API METHODS */
    /*******************************************/
}

module.exports = GridColumnRenderer;